import React, { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('authToken'));
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        return jwtDecode(token);
      } catch {
        return null;
      }
    }
    return null;
  });

  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem('favorites');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [votes, setVotes] = useState(() => {
    try {
      const raw = localStorage.getItem('votes');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const refreshFavorites = async () => {
    if (!authToken) return;

    try {
      const res = await fetch('/api/user/favorites', {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const data = await res.json();
      const enrichedFavorites = (data.favorites || []).map((f) => f.song || f);

      setFavorites(enrichedFavorites);
      localStorage.setItem('favorites', JSON.stringify(enrichedFavorites));
    } catch (err) {
      console.error('Failed to refresh favorites:', err);
      setFavorites([]);
      localStorage.removeItem('favorites');
    }
  };

  const refreshVotes = async () => {
    if (!authToken) return;

    try {
      const res = await fetch('/api/userVotes', {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const data = await res.json();
      setVotes(data.votes || {});
      localStorage.setItem('votes', JSON.stringify(data.votes || {}));
    } catch (err) {
      console.error('Failed to refresh votes:', err);
      setVotes({});
      localStorage.removeItem('votes');
    }
  };

  useEffect(() => {
    refreshFavorites();
    refreshVotes();
  }, [authToken]);

  function isTokenExpired(token) {
    try {
      const { exp } = jwtDecode(token);
      if (!exp) return true;
      // exp is in seconds, Date.now() in ms
      return Date.now() >= exp * 1000;
    } catch {
      return true;
    }
  }

  useEffect(() => {
    if (!authToken) return;

    if (isTokenExpired(authToken)) {
      logout();
      return;
    }

    // Optional: set a timer to auto-logout at the exact expiry time
    const { exp } = jwtDecode(authToken);
    const msUntilExpiry = exp * 1000 - Date.now();
    if (msUntilExpiry > 0) {
      const timeout = setTimeout(() => {
        logout();
      }, msUntilExpiry);
      return () => clearTimeout(timeout);
    }
  }, [authToken]);

  const login = (token) => {
    localStorage.setItem('authToken', token);
    setAuthToken(token);
    try {
      const decoded = jwtDecode(token);
      setUser(decoded);
    } catch {
      setUser(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('favorites');
    localStorage.removeItem('votes');
    setAuthToken(null);
    setUser(null);
    setFavorites([]);
    setVotes({});
  };

  const isFavorited = (songId) => {
    return favorites.some((fav) => fav.id === songId);
  };

  const toggleFavorite = async (songId) => {
    if (!authToken) return;

    const isAlready = isFavorited(songId);

    try {
      const url = isAlready
        ? `/api/user/favorites/${songId}`
        : `/api/user/favorites`;

      const options = {
        method: isAlready ? 'DELETE' : 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          ...(isAlready ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(isAlready ? {} : { body: JSON.stringify({ song_id: songId }) }),
      };

      const res = await fetch(url, options);

      if (res.ok) {
        if (isAlready) {
          const updated = favorites.filter((f) => f.id !== songId);
          setFavorites(updated);
          localStorage.setItem('favorites', JSON.stringify(updated));
        } else {
          const newRes = await fetch(`/api/song/${songId}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const { song } = await newRes.json();
          const updated = [...favorites, song];
          setFavorites(updated);
          localStorage.setItem('favorites', JSON.stringify(updated));
        }
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const voteForSong = async (songId, vote) => {
    if (!authToken || !Number.isInteger(vote) || vote < 1 || vote > 5) return;

    try {
      const res = await fetch(`/api/song/${songId}/vote`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ vote }),
      });

      if (res.ok) {
        const updatedVotes = { ...votes, [songId]: vote };
        setVotes(updatedVotes);
        localStorage.setItem('votes', JSON.stringify(updatedVotes));
      }
    } catch (err) {
      console.error('Failed to vote for song:', err);
    }
  };

  const removeVoteForSong = async (songId) => {
    if (!authToken) return;

    try {
      const res = await fetch(`/api/song/${songId}/vote`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (res.ok) {
        const updatedVotes = { ...votes };
        delete updatedVotes[songId];
        setVotes(updatedVotes);
        localStorage.setItem('votes', JSON.stringify(updatedVotes));
      }
    } catch (err) {
      console.error('Failed to remove vote:', err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authToken,
        user,
        login,
        logout,
        favorites,
        isFavorited,
        toggleFavorite,
        refreshFavorites,
        votes,
        refreshVotes,
        voteForSong,
        removeVoteForSong,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
